import { useState, useRef, useEffect, useLayoutEffect } from "react";
import {
  Search, Plus, Printer, Pencil, ArrowUp, ArrowDown, RotateCcwClock, X, CalendarDays, Trash, Maximize2, Minimize2,
  ClipboardPen, Save,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useDrugs } from "@/lib/queries";
import { useCreatePrescription } from "@/lib/mutations";
import type { MedicineRow, PrescriptionRow, VisitDetail, EmrNote } from "@/lib/queries";
import { EditMedicineDialog } from "./EditMedicineDialog";
import { CopyPreviousRxDialog } from "./CopyPreviousRxDialog";
import { PrescriptionTemplatePickerDialog } from "./PrescriptionTemplatePickerDialog";
import { SaveAsPrescriptionTemplateDialog } from "./SaveAsPrescriptionTemplateDialog";
import { FrequencyInput } from "./FrequencyInput";
import { ReviewBar } from "./VisitColumns";
import { useDropdownNav } from "@/hooks/useDropdownNav";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { createPrescriptionPdf, type PrescriptionMedicine } from "@/lib/pdf/prescriptionPdf";
import { printPdf } from "@/lib/print";
import { printCompositionPref, setPrintCompositionPref } from "@/lib/rxPrint";
import { fmtDate, dateIST } from "@emr/shared/india";
import { isValidFrequency, normalizeFrequency, rebaseTaper, type TaperStep } from "@emr/shared";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  type DraftMedicine, type DraftTaperRow, TAPER_GRID, TAPER_GRID_DRUG,
  daysFromDuration, addDays, stepToDate, rechainTaperRows, newTaperRow,
  draftToTaperSteps,
} from "./taperDraft";
import { TaperRowDrugSearch } from "./TaperRowDrugSearch";

interface Props {
  visit: VisitDetail;
  prescription: PrescriptionRow | null;
  note: EmrNote | null;
  // Bumped by EmrVisitPage after explicit re-hydration (global Save,
  // copy-from-previous) — the only moments this table re-adopts the server's
  // medicines. Background refetches must not re-adopt (they would clobber
  // in-flight local edits with a stale echo).
  hydrateSignal: number;
  // Reports the LIVE medicine rows to the page on every change, so
  // page-level prints (Print → Prescription / Visit Summary) don't read a
  // cached aggregate that lags the table's in-flight rows.
  onMedicinesChange?: (rows: MedicineRow[]) => void;
  // Live diagnosis from the visit page (may be ahead of the cached note by
  // up to the 1s notes-autosave debounce). Used for this table's Print and
  // Save-to-cloud PDFs.
  liveDiagnosis?: string | null;
  // Live review state (may be ahead of the cached visit row).
  liveReviewNeeded?: boolean;
  liveReviewDate?: string | null;
  // Why the live review date is unusable (blocked/off day), precomputed by
  // the parent via dayUnavailableReason() — null = fine. When set, the
  // Print button defers to the parent's ReviewDateDialog instead of
  // printing a PDF that carries an unusable review date (the server would
  // 422 the review save; the ReviewBar already shows the red warning).
  liveReviewDateUnavailable?: { kind: "off" | "blocked"; reason: string | null } | null;
  // Called when the table's Print button is clicked but the visit has no
  // review date selected (reviewNeeded=false && reviewDate empty), or the
  // date is set but unusable (blocked/off day). The parent opens its
  // ReviewDateDialog; on Save & Print the dialog persists the review and
  // calls buildAndPrintPrescription, which reads this table's live
  // medicines via onMedicinesChange — so the print uses the current rows,
  // not a stale cache.
  onRequestReviewDate?: () => void;
  // Review state for the inline ReviewBar shown at the bottom of the
  // fullscreen prescription card. The parent passes its live reviewNeeded /
  // reviewDate + setters so the bar edits the same state as the page-level
  // ReviewBar (below the prescription card in normal mode).
  reviewNeeded: boolean;
  reviewDate: string; // YYYY-MM-DD
  onReviewNeeded: (v: boolean) => void;
  onReviewDate: (v: string) => void;
}

// One row of the escalation / de-escalation protocol builder (draft state) and
// the whole draft/taper scaffolding (interfaces, grid templates, date + rechain
// helpers, row builder, TaperStep conversion, per-row drug search) now live in
// ./taperDraft — shared with EditMedicineDialog and the prescription-templates
// page (Batch 7 Wave 3 dedupe). The discharge-summary module keeps its own
// clones by design (user exemption 2026-10-16).

export function PrescriptionTable({ visit, prescription, note, hydrateSignal, onMedicinesChange, liveDiagnosis, liveReviewNeeded, liveReviewDate, liveReviewDateUnavailable, onRequestReviewDate, reviewNeeded, reviewDate, onReviewNeeded, onReviewDate }: Props) {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("prescriptions.create");
  const canEdit = isAdmin || permissions.has("prescriptions.edit");
  const [query, setQuery] = useState("");
  // Expand the prescription card to fullscreen (excluding the navbar and
  // sidebar) so the user can type and review drugs comfortably. Toggled by
  // clicking the "Prescription" heading; closed with the [X] button or Esc.
  const [fullscreen, setFullscreen] = useState(false);
  // Pixel offset of the overlay's left edge — matches the current sidebar
  // width (w-16 = 64px collapsed, w-60 = 240px expanded) so the overlay
  // never covers the sidebar. Measured at toggle-on time so it tracks the
  // user's current sidebar state without needing a prop drill-down.
  const [sidebarWidth, setSidebarWidth] = useState(0);
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);
  useLayoutEffect(() => {
    if (!fullscreen) return;
    const aside = document.querySelector("aside");
    setSidebarWidth(aside ? aside.getBoundingClientRect().width : 0);
  }, [fullscreen]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [draft, setDraft] = useState<DraftMedicine | null>(null);
  const [medicines, setMedicines] = useState<MedicineRow[]>(prescription?.medicines ?? []);
  const [editing, setEditing] = useState<MedicineRow | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [globalDays, setGlobalDays] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  // Prescription templates: picker (search + dropdown of stored templates)
  // and save-as-template (snapshots the live rows under a name).
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  // "Print drug composition" — sticky printing preference (default checked).
  // Shared lib so every prescription print honors the same last choice.
  const [printComposition, setPrintComposition] = useState<boolean>(printCompositionPref);
  const togglePrintComposition = (v: boolean) => {
    setPrintComposition(v);
    setPrintCompositionPref(v);
  };
  const searchRef = useRef<HTMLInputElement>(null);
  // Latest medicines mirror for debounced persists (the days debounce must
  // send the CURRENT table, not the rows captured when typing started —
  // e.g. a reorder during the debounce window would otherwise be reverted
  // server-side by the delayed persist).
  const medicinesRef = useRef(medicines);
  // eslint-disable-next-line react-hooks/refs -- deliberate render-time mirror so debounced persists always send the latest rows (React Compiler is not in use)
  medicinesRef.current = medicines;

  // Report live rows to the page (for page-level prints).
  useEffect(() => {
    onMedicinesChange?.(medicinesRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicines]);

  // Debounced search query (250ms) — fires one /drugs?q= per typing pause
  // instead of one per keystroke (see useDebouncedValue). The dropdown's
  // open/close stays driven by the live `query`.
  const debouncedQuery = useDebouncedValue(query, 250);
  const { data: drugsData, isLoading: drugsLoading } = useDrugs(debouncedQuery.length >= 3 ? debouncedQuery : "");
  const drugs = (drugsData && "rows" in drugsData ? drugsData.rows : Array.isArray(drugsData) ? drugsData : []) as { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }[];

  // Dropdown items: up to 10 drug matches + 1 "Add custom drug" entry.
  // The custom-drug entry is always present when the dropdown is open so
  // Enter on it adds the typed text as a custom drug (matching the old
  // click behaviour).
  const drugResults = drugs.slice(0, 10);
  const drugListCount = drugResults.length + 1; // +1 for the custom-drug entry

  // Keyboard navigation: ArrowUp/Down to move highlight, Enter to trigger
  // the same action a click would (startAdd with the drug, or startAdd
  // undefined for the custom-drug entry), Escape to close.
  const { highlight: drugHighlight, highlightRef: drugHighlightRef, onKeyDown: onDrugSearchKeyDown } = useDropdownNav({
    count: showDropdown && query.length >= 3 ? drugListCount : 0,
    onSelect: (i) => {
      if (i < drugResults.length) startAdd(drugResults[i]);
      else startAdd(undefined);
    },
    inputRef: searchRef,
    onEscape: () => setShowDropdown(false),
  });

  const createMut = useCreatePrescription(visit.id);

  // Serialized persist queue. Each table mutation (add, reorder, days, edit,
  // delete, copy) replaces the full medicine list server-side; sending them
  // concurrently can interleave and land out of order. A promise chain
  // guarantees order: every persist awaits the previous one before firing,
  // and always carries the latest local rows.
  // chainPending counts in-flight/queued persists: while > 0 the server is
  // behind the local table, so re-adoption from a refetch would clobber the
  // newer local rows.
  const persistChain = useRef<Promise<unknown>>(Promise.resolve());
  const chainPending = useRef(0);
  const schedulePersist = (rows: MedicineRow[]) => {
    chainPending.current += 1;
    persistChain.current = persistChain.current
      .catch(() => undefined)
      .then(() =>
        createMut.mutateAsync({
          medicines: rows.map((m) => ({
            drugId: m.drugId,
            name: m.name,
            composition: m.composition,
            formulation: m.formulation,
            strength: m.strength,
            frequency: m.frequency,
            taper: m.taper,
            duration: m.duration,
            instructions: m.instructions,
            serialNo: m.serialNo,
          })),
        }),
      )
      .catch(() => undefined)
      .finally(() => {
        chainPending.current -= 1;
      });
  };

  // Initial adoption of the server prescription — ONCE per visit (even when
  // there is no prescription yet, so the refetch echo after the first drug is
  // added doesn't re-adopt and clobber concurrent local rows).
  const adoptedVisit = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (adoptedVisit.current === visit.id) return;
    adoptedVisit.current = visit.id;
    setMedicines(prescription?.medicines ?? []);
  }, [prescription, visit.id]);

  // Re-adopt server medicines ONLY when the page signals an explicit
  // re-hydration (global Save / copy-from-previous completed and the
  // aggregate was refetched). If persists are queued/in-flight, the LOCAL
  // rows are newer than the refetched snapshot — skip adoption and keep
  // local (the chain is writing them server-side; the mutation's onSuccess
  // invalidation refreshes the cache afterwards). A pending days debounce
  // is flushed first so its edit still reaches the server.
  const lastHydrateSignal = useRef(0);
  useEffect(() => {
    if (hydrateSignal === 0 || hydrateSignal === lastHydrateSignal.current) return;
    lastHydrateSignal.current = hydrateSignal;
    // Flush a pending days debounce (the user may have clicked Save within
    // the window) so its edit reaches the server.
    if (daysDebounceRef.current) {
      clearTimeout(daysDebounceRef.current);
      daysDebounceRef.current = null;
      schedulePersist(medicinesRef.current);
      return; // local is ahead of the snapshot — keep it
    }
    if (chainPending.current > 0) return; // chain still writing — keep local
    setMedicines(prescription?.medicines ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrateSignal]);

  const startAdd = (drug?: { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }) => {
    setDraft({
      drugId: drug?.id ?? null,
      name: drug?.name ?? query,
      composition: drug?.composition ?? null,
      formulation: (drug?.formulation as MedicineRow["formulation"]) ?? "Tablet",
      strength: drug?.strength ?? "",
      frequency: "",
      taper: null,
      days: globalDays,
      instructions: "",
      perStrength: false,
    });
    setShowDropdown(false);
  };

  // ---- Escalation / de-escalation protocol builder (draft state) ----
  // Build a draft taper row. In per-strength mode, the row's drug fields
  // default to the draft's row-level drug (so the user only edits the rows
  // that differ); otherwise they stay empty (the step inherits the row's
  // drug at print time via stepDrugName()). newTaperRow now lives in
  // ./taperDraft (shared with the edit dialog + templates page).
  const enableTaper = () => {
    if (!draft) return;
    setDraft({
      ...draft,
      taper: draft.taper ?? [newTaperRow(dateIST(), draft, draft.perStrength)],
    });
  };
  const disableTaper = () => {
    if (!draft) return;
    setDraft({ ...draft, taper: null });
  };
  // Toggle per-strength mode. When turning ON, seed every existing row with
  // the row-level drug so the user starts from the draft's drug and edits
  // only the rows that differ. When turning OFF, clear the per-step overrides
  // (steps fall back to the row-level drug at print time).
  const togglePerStrength = (checked: boolean) => {
    if (!draft) return;
    if (!checked) {
      setDraft({
        ...draft,
        perStrength: false,
        taper: (draft.taper ?? []).map((r) => ({ ...r, name: "", strength: "", composition: null, formulation: null, drugId: null })),
      });
    } else {
      setDraft({
        ...draft,
        perStrength: true,
        taper: (draft.taper ?? []).map((r) => ({ ...r, name: r.name || draft.name, strength: r.strength || draft.strength, composition: r.composition ?? draft.composition, formulation: r.formulation ?? draft.formulation, drugId: r.drugId ?? draft.drugId })),
      });
    }
  };
  // Edit one taper row. A from-date or days change re-chains every FOLLOWING
  // row's from-date (prev To + 1) so start/end stay continuous — the same
  // rule the "+" append uses (user request 2026-10-30: changing days must
  // recalculate start/end days of the following periods).
  const setTaperRow = (key: string, patch: Partial<DraftTaperRow>) => {
    if (!draft?.taper) return;
    const idx = draft.taper.findIndex((r) => r.key === key);
    if (idx < 0) return;
    const patched = draft.taper.map((r) => (r.key === key ? { ...r, ...patch } : r));
    setDraft({ ...draft, taper: "fromDate" in patch || "days" in patch ? rechainTaperRows(patched, idx) : patched });
  };
  const removeTaperRow = (key: string) => {
    if (!draft?.taper) return;
    setDraft({ ...draft, taper: draft.taper.filter((r) => r.key !== key) });
  };
  const addTaperRow = () => {
    if (!draft?.taper) return;
    const last = draft.taper[draft.taper.length - 1];
    // Next From = previous row's To + 1 day.
    let fromDate = dateIST();
    if (last) {
      const to = stepToDate(last.fromDate, last.days);
      if (to) fromDate = addDays(to, 1);
    }
    // In per-strength mode, the new row inherits the LAST row's drug (not the
    // draft-level drug) — that's the most common case (you keep going on the
    // same strength you just added).
    const inherit = draft.perStrength && last
      ? { name: last.name || draft.name, strength: last.strength || draft.strength, composition: last.composition ?? draft.composition, formulation: last.formulation ?? draft.formulation, drugId: last.drugId ?? draft.drugId }
      : draft;
    setDraft({
      ...draft,
      taper: [...draft.taper, newTaperRow(fromDate, inherit as DraftMedicine, draft.perStrength)],
    });
  };

  const confirmAdd = () => {
    if (!draft || !draft.name.trim()) return;
    const freq = normalizeFrequency(draft.frequency);
    if (draft.taper) {
      // Validate every protocol row.
      for (const [i, r] of draft.taper.entries()) {
        if (!r.fromDate) { toast.error(`Protocol row ${i + 1}: pick a From date`); return; }
        if (!r.days || parseInt(r.days, 10) < 1) { toast.error(`Protocol row ${i + 1}: enter the number of days`); return; }
        if (!normalizeFrequency(r.frequency) || !isValidFrequency(normalizeFrequency(r.frequency))) {
          toast.error(`Protocol row ${i + 1}: enter a frequency like 1-1-1-1`);
          return;
        }
        if (draft.perStrength && !r.name.trim()) {
          toast.error(`Protocol row ${i + 1}: pick a drug for this row`);
          return;
        }
      }
      if (draft.taper.length === 0) { toast.error("Add at least one protocol row"); return; }
    } else {
      // Plain drug: frequency and instructions may not BOTH be blank.
      if (!freq && !draft.instructions.trim()) {
        toast.error("Enter a frequency or instructions");
        return;
      }
      if (!isValidFrequency(freq)) {
        toast.error("Frequency looks invalid — use numbers like 1-1-1 or 1.5-0-1");
        return;
      }
    }
    const taperSteps: TaperStep[] | null = draftToTaperSteps(draft);
    const newRow: MedicineRow = {
      id: crypto.randomUUID(),
      prescriptionId: prescription?.id ?? "draft",
      drugId: draft.drugId,
      serialNo: medicines.length + 1,
      name: draft.name,
      composition: draft.composition,
      formulation: draft.formulation,
      strength: draft.strength || null,
      frequency: taperSteps ? null : (freq || null),
      taper: taperSteps,
      duration: taperSteps ? null : (draft.days ? `${draft.days} days` : null),
      instructions: draft.instructions || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const next = [...medicines, newRow];
    setMedicines(next);
    schedulePersist(next);
    setDraft(null);
    setQuery("");
    // Return cursor to search box for the next drug.
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= medicines.length) return;
    const next = [...medicines];
    [next[idx], next[target]] = [next[target], next[idx]];
    const renumbered = next.map((row, i) => ({ ...row, serialNo: i + 1 }));
    setMedicines(renumbered);
    schedulePersist(renumbered);
  };

  // Per-row days: update locally at once, but debounce the server persist
  // (750ms) so typing "15" doesn't fire a POST per keystroke. The debounced
  // persist sends the LATEST medicines (medicinesRef), so a reorder or other
  // edit during the debounce window isn't reverted server-side.
  const daysDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setRowDays = (id: string, days: string) => {
    const next = medicines.map((row) =>
      row.id === id ? { ...row, duration: days ? `${days} days` : null } : row,
    );
    setMedicines(next);
    if (daysDebounceRef.current) clearTimeout(daysDebounceRef.current);
    // eslint-disable-next-line react-hooks/immutability -- deliberate: debounce timer handle lives in a ref (state here would re-render per keystroke); see medicinesRef comment above
    daysDebounceRef.current = setTimeout(() => schedulePersist(medicinesRef.current), 750);
  };

  const applyGlobalDays = () => {
    const n = parseInt(globalDays, 10);
    if (!n || n <= 0) {
      toast.error("Enter a valid number of days");
      return;
    }
    if (medicines.length === 0) {
      toast.info("No medicines yet — this will be the default for new drugs.");
      return;
    }
    const next = medicines.map((row) => ({ ...row, duration: `${n} days` }));
    setMedicines(next);
    schedulePersist(next);
    toast.success(`Applied ${n} days to all medicines`);
  };

  const onCopyFromPrevious = (src: PrescriptionRow) => {
    // Taper dates are copied VERBATIM (user decision 2026-10-30): the source
    // visit's fromDate values are intentionally preserved so the doctor can
    // see exactly what the old protocol was and edit the dates by hand —
    // never rebased here (rebasing would hide what needs changing).
    const copied: MedicineRow[] = src.medicines.map((m, i) => ({
      ...m,
      id: crypto.randomUUID(),
      prescriptionId: prescription?.id ?? "draft",
      serialNo: i + 1,
    }));
    setMedicines(copied);
    schedulePersist(copied);
    toast.success("Prescription copied");
  };

  // Append a stored template's medicines to the CURRENT rows (user decision
  // 2026-10-30: append, not replace). Serial numbers continue from the end
  // so the appended block reads as a continuation. Same persist path as
  // copy-old — the serialized chain + hydration guards are untouched.
  // Taper dates are REBASED to today (IST): the stored fromDate values are
  // frozen at template-save time, so inserting verbatim would carry stale
  // dates into the new prescription. Row 1 starts today; every following
  // row starts the day after the previous To (fromDate + days - 1) — the
  // same chaining rule the add-panel +/append uses (user request 2026-10-30).
  const onApplyTemplate = (tpl: { name: string; medicines: Omit<MedicineRow, "id" | "prescriptionId" | "serialNo" | "createdAt" | "updatedAt">[] }) => {
    if (tpl.medicines.length === 0) {
      toast.error("This template has no medicines");
      return;
    }
    const today = dateIST();
    const base = medicines.length;
    const appended: MedicineRow[] = tpl.medicines.map((m, i) => ({
      ...m,
      id: crypto.randomUUID(),
      prescriptionId: prescription?.id ?? "draft",
      serialNo: base + i + 1,
      taper: m.taper ? rebaseTaper(m.taper, today) : m.taper,
    }));
    const next = [...medicines, ...appended];
    setMedicines(next);
    schedulePersist(next);
    toast.success(`Template "${tpl.name}" added (${tpl.medicines.length} medicine${tpl.medicines.length === 1 ? "" : "s"})`);
  };

  const onEditSave = (m: Partial<MedicineRow>) => {
    if (!editing) return;
    const next = medicines.map((row) => (row.id === editing.id ? { ...row, ...m } : row));
    setMedicines(next);
    schedulePersist(next);
  };

  const onEditDelete = () => {
    if (!editing) return;
    const filtered = medicines.filter((row) => row.id !== editing.id);
    const renumbered = filtered.map((row, i) => ({ ...row, serialNo: i + 1 }));
    setMedicines(renumbered);
    schedulePersist(renumbered);
  };

  const print = async () => {
    if (medicines.length === 0) {
      toast.error("No medicines to print");
      return;
    }
    // If the visit has no review date selected — or the date is set but
    // unusable (blocked/off day, precomputed by the parent) — defer to the
    // parent's ReviewDateDialog (same prompt as the page-level Print →
    // Prescription). The dialog persists the review and calls
    // buildAndPrintPrescription, which reads this table's live medicines
    // via onMedicinesChange.
    const effReviewNeeded = liveReviewNeeded ?? visit.reviewNeeded;
    const effReviewDate = liveReviewDate ?? visit.reviewDate;
    if (!effReviewNeeded && (!effReviewDate || liveReviewDateUnavailable)) {
      onRequestReviewDate?.();
      return;
    }
    const doc = await createPrescriptionPdf({
      patientName: visit.patientName ?? "Patient",
      clinicUhid: visit.clinicUhid ?? "",
      patientDob: visit.patientDob,
      patientGender: visit.patientGender,
      checkInAt: visit.checkInAt,
      // Live diagnosis (may be ahead of the cached note by the 1s autosave
      // debounce); fall back to the cached note.
      diagnosis: liveDiagnosis ?? note?.diagnosis ?? null,
      medicines: medicines as PrescriptionMedicine[],
      showComposition: printComposition,
      reviewNeeded: effReviewNeeded,
      reviewDate: effReviewDate,
      doctorName: visit.doctorName,
      doctorQualifications: visit.doctorQualifications,
      doctorRegNumber: visit.doctorRegNumber,
    });
    printPdf(doc);
  };

  return (
    <Card
      className={fullscreen
        ? "fixed right-0 top-14 bottom-0 z-40 flex flex-col rounded-none border-0 shadow-lg"
        : "flex min-h-[40vh] flex-col"
      }
      style={fullscreen ? { left: sidebarWidth } : undefined}
    >
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setFullscreen((f) => !f)}
            title={fullscreen ? "Exit full screen" : "Expand prescription to full screen"}
            className="group flex items-center gap-1.5 rounded-md px-1 py-0.5 -mx-1 text-maroon-700 hover:bg-cream-100 hover:text-maroon-800 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-maroon-400 dark:text-amber-500 dark:hover:bg-dark-700"
          >
            <CardTitle className="cursor-pointer select-none">Prescription</CardTitle>
            {fullscreen ? <Minimize2 size={14} className="opacity-60 transition-opacity group-hover:opacity-100" /> : <Maximize2 size={14} className="opacity-60 transition-opacity group-hover:opacity-100" />}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canCreate && (
            <Button size="sm" variant="outline" onClick={() => setTemplatesOpen(true)} title="Insert a stored prescription template">
              <ClipboardPen size={14} className="mr-1" /> Templates
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setCopyOpen(true)}>
            <RotateCcwClock size={14} className="mr-1" /> Copy old prescription
          </Button>
          {canCreate && (
            <Button size="sm" variant="outline" onClick={() => setSaveTplOpen(true)} disabled={medicines.length === 0} title="Save the current medicines as a named template">
              <Save size={14} className="mr-1" /> Save as template
            </Button>
          )}
          <div className="flex items-center gap-1.5" title="Print the drug composition (ingredients) on prescriptions — remembered for future prescriptions">
            <Checkbox
              id="print-composition"
              checked={printComposition}
              onCheckedChange={(v) => togglePrintComposition(v === true)}
            />
            <Label htmlFor="print-composition" className="cursor-pointer text-xs font-normal">
              Print drug composition
            </Label>
          </div>
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min={1}
              placeholder="Days"
              className="h-8 w-20"
              value={globalDays}
              onChange={(e) => setGlobalDays(e.target.value)}
            />
            <Button size="sm" variant="outline" className="h-8" onClick={applyGlobalDays}>
              Apply to all
            </Button>
          </div>
          <Button size="sm" variant="outline" onClick={print} disabled={medicines.length === 0}>
            <Printer size={14} className="mr-1" /> Print
          </Button>
          {fullscreen && (
            <Button
              size="sm"
              variant="outline"
              className="ml-1 h-8 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-800 dark:text-red-400 dark:hover:bg-dark-700"
              onClick={() => setFullscreen(false)}
              title="Close full screen (Esc)"
            >
              <X size={14} className="mr-1" /> Close
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-3 overflow-y-auto">
        {canCreate && (
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <Input
              ref={searchRef}
              placeholder="Search drug name or composition (3+ chars)…"
              className="pl-9"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowDropdown(e.target.value.length >= 3); }}
              onFocus={() => query.length >= 3 && setShowDropdown(true)}
              onKeyDown={onDrugSearchKeyDown}
            />
            {showDropdown && query.length >= 3 && (
              <div className="visible-scrollbar absolute z-20 mt-1 max-h-60 w-full overscroll-contain overflow-y-auto rounded-md border border-cream-200 bg-cream-100 shadow-lg dark:border-dark-700 dark:bg-dark-800">
                {drugsLoading ? (
                  <div className="p-2"><Skeleton className="h-6" /></div>
                ) : drugs.length === 0 ? (
                  <button
                    ref={drugHighlight === 0 ? drugHighlightRef : undefined}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                      drugHighlight === 0 ? "bg-cream-200 dark:bg-dark-700" : ""
                    }`}
                    onClick={() => startAdd(undefined)}
                  >
                    <Plus size={14} className="text-maroon-600" /> Add "{query}" as custom drug
                  </button>
                ) : (
                  <>
                    {drugResults.map((d, idx) => (
                      <button
                        key={d.id}
                        ref={idx === drugHighlight ? drugHighlightRef : undefined}
                        className={`flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                          idx === drugHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                        }`}
                        onClick={() => startAdd(d)}
                      >
                        <span className="font-medium text-ink dark:text-cream-50">{d.name}</span>
                        <span className="text-xs text-muted">
                          {[d.composition, d.formulation, d.strength].filter(Boolean).join(" · ")}
                        </span>
                      </button>
                    ))}
                    <button
                      ref={drugResults.length === drugHighlight ? drugHighlightRef : undefined}
                      className={`flex w-full items-center gap-2 border-t border-cream-200 px-3 py-2 text-left text-sm text-maroon-600 hover:bg-cream-200 dark:border-dark-700 dark:hover:bg-dark-700 ${
                        drugResults.length === drugHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                      }`}
                      onClick={() => startAdd(undefined)}
                    >
                      <Plus size={14} /> Add "{query}" as custom drug
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {draft && (
          <div className="rounded-lg border border-maroon-200 bg-cream-50/50 p-3 dark:border-amber-700/40 dark:bg-dark-800/50">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium">
                {draft.name}{draft.strength ? ` — ${draft.strength}` : ""}
              </span>
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Cancel</Button>
            </div>

            <div className="mb-2 flex items-center gap-2">
              <Checkbox
                id="taper-toggle"
                checked={!!draft.taper}
                onCheckedChange={(checked) => (checked ? enableTaper() : disableTaper())}
              />
              <Label htmlFor="taper-toggle" className="text-xs cursor-pointer">
                Escalation / De-escalation protocol
              </Label>
              {draft.taper && (
                <>
                  <Checkbox
                    id="per-strength-toggle"
                    className="ml-4"
                    checked={draft.perStrength}
                    onCheckedChange={(checked) => togglePerStrength(checked === true)}
                  />
                  <Label htmlFor="per-strength-toggle" className="text-xs cursor-pointer">
                    Allow different strengths per row
                  </Label>
                </>
              )}
            </div>

            {draft.taper ? (
              <div className="space-y-1.5">
                {draft.perStrength ? (
                  <div className={`${TAPER_GRID_DRUG} text-xs text-muted`}>
                    <span>Drug</span>
                    <span>From date</span>
                    <span>Frequency</span>
                    <span>Days</span>
                    <span>To date</span>
                    <span>Instructions</span>
                    <span className="text-center">+</span>
                    <span className="text-center">×</span>
                  </div>
                ) : (
                  <div className={`${TAPER_GRID} text-xs text-muted`}>
                    <span>From date</span>
                    <span>Frequency</span>
                    <span>Days</span>
                    <span>To date</span>
                    <span>Instructions</span>
                    <span className="text-center">+</span>
                    <span className="text-center">×</span>
                  </div>
                )}
                {draft.taper.map((r) => {
                  const to = stepToDate(r.fromDate, r.days);
                  return (
                    <div key={r.key} className={draft.perStrength ? TAPER_GRID_DRUG : TAPER_GRID}>
                      {draft.perStrength && (
                        <TaperRowDrugSearch
                          rowKey={r.key}
                          value={r.name}
                          onPick={(d) => setTaperRow(r.key, {
                            drugId: d.id || null,
                            name: d.name,
                            strength: d.strength ?? r.strength,
                            composition: d.composition ?? r.composition,
                            formulation: (d.formulation as MedicineRow["formulation"]) ?? r.formulation,
                          })}
                        />
                      )}
                      <Input
                        type="date"
                        className="h-8"
                        value={r.fromDate}
                        onChange={(e) => setTaperRow(r.key, { fromDate: e.target.value })}
                      />
                      <FrequencyInput
                        className="h-8"
                        value={r.frequency}
                        onChange={(v) => setTaperRow(r.key, { frequency: v })}
                      />
                      <Input
                        type="number"
                        min={1}
                        className="h-8"
                        value={r.days}
                        onChange={(e) => setTaperRow(r.key, { days: e.target.value })}
                      />
                      <div className="flex h-8 items-center gap-1 text-sm text-muted">
                        <CalendarDays size={13} className="shrink-0" />
                        <span className="whitespace-nowrap">{to ? fmtDate(to) : "—"}</span>
                      </div>
                      <Input
                        className="h-8"
                        placeholder="With breakfast"
                        value={r.instructions}
                        onChange={(e) => setTaperRow(r.key, { instructions: e.target.value })}
                      />
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-maroon-600" onClick={addTaperRow} title="Add next row">
                        <Plus size={14} />
                      </Button>
                      <Button
                        size="icon" variant="ghost" className="h-8 w-8 text-red-600"
                        onClick={() => removeTaperRow(r.key)}
                        disabled={draft.taper!.length === 1}
                        title="Remove row"
                      >
                        <X size={14} />
                      </Button>
                    </div>
                  );
                })}
                <div className="flex items-center gap-1.5 text-xs text-muted">
                  <Plus size={12} className="text-maroon-600" />
                  {draft.perStrength
                    ? <>Use <b>+</b> to append a row. Search a different strength in each row's Drug box; consecutive same-strength rows print under one header.</>
                    : <>Use <b>+</b> on a row to append the next period (its From date starts the day after the previous To date).</>}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-[180px_110px_1fr_auto] items-end gap-2">
                <div>
                  <Label className="text-xs">Frequency</Label>
                  <FrequencyInput
                    value={draft.frequency}
                    onChange={(v) => setDraft({ ...draft, frequency: v })}
                    onCommit={confirmAdd}
                  />
                </div>
                <div>
                  <Label className="text-xs">Days</Label>
                  <Input
                    type="number"
                    min={1}
                    value={draft.days}
                    onChange={(e) => setDraft({ ...draft, days: e.target.value })}
                    placeholder="5"
                  />
                </div>
                <div>
                  <Label className="text-xs">Instructions</Label>
                  <Input
                    value={draft.instructions}
                    onChange={(e) => setDraft({ ...draft, instructions: e.target.value })}
                    placeholder="e.g. After meals (optional)"
                  />
                </div>
                <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={confirmAdd}>
                  <Plus size={14} className="mr-1" /> Add to table
                </Button>
              </div>
            )}

            {draft.taper && (
              <div className="mt-2 flex justify-end">
                <Button size="sm" className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={confirmAdd}>
                  <Plus size={14} className="mr-1" /> Add to table
                </Button>
                <span className="ml-3 self-center text-xs text-muted">
                  Days and frequency come from the protocol rows above.
                </span>
              </div>
            )}
          </div>
        )}

        {medicines.length === 0 ? (
          <div className="rounded-md bg-cream-50 p-6 text-center text-sm text-muted dark:bg-dark-800">
            No medicines added yet. Search above to add drugs, or copy an old prescription.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
                <TableHead className="w-12">Sr #</TableHead>
                <TableHead>Drug</TableHead>
                <TableHead>Instructions</TableHead>
                <TableHead className="w-28">Frequency</TableHead>
                <TableHead className="w-24">Days</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {medicines.map((m, idx) => (
                <TableRow key={m.id} className="even:bg-cream-50/50">
                  <TableCell className="font-mono text-xs">{m.serialNo}</TableCell>
                  <TableCell
                    className={canEdit ? "cursor-pointer" : ""}
                    onClick={() => { if (canEdit) { setEditing(m); setEditOpen(true); } }}
                  >
                    <div className="font-medium text-ink dark:text-cream-50">{m.name}</div>
                    {m.composition && <div className="text-xs text-muted">{m.composition}</div>}
                    {m.taper && (
                      <div className="text-xs text-maroon-600 dark:text-amber-500">
                        Escalation / De-escalation protocol — {m.taper.length} period(s)
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{m.instructions ?? "—"}</TableCell>
                  <TableCell className="break-all font-mono text-xs">{m.taper ? "—" : (m.frequency ?? "—")}</TableCell>
                  <TableCell>
                    {m.taper ? (
                      <Badge variant="outline" className="border-maroon-300 text-maroon-700 dark:border-amber-600 dark:text-amber-500">Taper</Badge>
                    ) : (
                      <Input
                        type="number"
                        min={1}
                        className="h-8 w-16"
                        value={daysFromDuration(m.duration)}
                        onChange={(e) => setRowDays(m.id, e.target.value)}
                        disabled={!canEdit}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === 0 || !canEdit} onClick={() => move(idx, -1)}>
                        <ArrowUp size={13} />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === medicines.length - 1 || !canEdit} onClick={() => move(idx, 1)}>
                        <ArrowDown size={13} />
                      </Button>
                      {canEdit && (
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setEditing(m); setEditOpen(true); }}>
                          <Pencil size={13} />
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          size="icon" variant="ghost" className="h-6 w-6 text-red-600" title="Delete medicine"
                          onClick={() => {
                            const filtered = medicines.filter((row) => row.id !== m.id);
                            const renumbered = filtered.map((row, i) => ({ ...row, serialNo: i + 1 }));
                            setMedicines(renumbered);
                            schedulePersist(renumbered);
                            toast.success(`${m.name} removed`);
                          }}
                        >
                          <Trash size={13} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <EditMedicineDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          medicine={editing}
          onSave={onEditSave}
          onDelete={onEditDelete}
        />

        <CopyPreviousRxDialog
          open={copyOpen}
          onOpenChange={setCopyOpen}
          patientId={visit.patientId}
          currentVisitId={visit.id}
          onCopy={onCopyFromPrevious}
        />

        <PrescriptionTemplatePickerDialog
          open={templatesOpen}
          onOpenChange={setTemplatesOpen}
          onPick={onApplyTemplate}
        />

        <SaveAsPrescriptionTemplateDialog
          open={saveTplOpen}
          onOpenChange={setSaveTplOpen}
          medicines={medicines}
        />
      </CardContent>

      {/* Inline review bar — only visible in fullscreen so the doctor can
          set the review date without leaving the prescription card. Edits
          the same state as the page-level ReviewBar (below the card in
          normal mode), via the setters passed from emr-visit. */}
      {fullscreen && (
        <div className="shrink-0 border-t border-cream-200 px-6 py-2 dark:border-dark-700">
          <ReviewBar
            visitId={visit.id}
            reviewNeeded={reviewNeeded}
            reviewDate={reviewDate}
            onReviewNeeded={onReviewNeeded}
            onReviewDate={onReviewDate}
          />
        </div>
      )}
    </Card>
  );
}
